import { HttpClient } from '@angular/common/http';
import { Injectable, TransferState, inject } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';

import { validateBucketContent } from '@23degrees/shared-code/content/validateBucket';
import { Content } from '@23degrees/shared-code/models';
import { ContentResponse } from '@23degrees/shared/models/content-response.model';

import { EnvironmentService } from '../env/env.service';
import { GlobalService } from '../global/global.service';
import { cleanSlug } from '@23degrees/utilities/content.utils';
import { generateTimeCache } from '@23degrees/utilities/cache.utils';
import { pSingleton } from '@23degrees/utilities/promise.utils';

@Injectable({ providedIn: 'root' })
export class ContentService {
  private http = inject(HttpClient);
  private globalService = inject(GlobalService);
  transferState: TransferState = inject(TransferState);

  private envService: EnvironmentService = inject(EnvironmentService);

  // if you use this route definitions
  // they will never work in ssr
  API_PATH = this.envService.CONTENT_API;
  PUB_PATH = '/services/public';
  GARBAGE_PATH = '/services/pub/api/v1/garbage';
  CONTENT_STUB_PATH = '/services/pub/api/v1/content/stub';
  MDC_PATH = '/services/pub/api/v1/mdc';
  API_V2_PATH = `${this.envService.APIV2}/content`;

  public createDataSetVersionedPromise = pSingleton(this._createDataSetVersionedPromise);
  public cloneDataSetVersionedPromise = pSingleton(this._cloneDataSetVersionedPromise);

  search(query: string, space: string = 'public', page: number = 0, page_size: number = 12, userslug?: string): Observable<any> {
    if (userslug) {
      return this.http.get<any>(`${this.API_V2_PATH}/search?query=${query}&space=${space}&page=${page}&page_size=${page_size}&userslug=${userslug}`);
    }
    return this.http.get<any>(`${this.API_V2_PATH}/search?query=${query}&space=${space}&page=${page}&page_size=${page_size}`);
  }

  /**
   * [getPublicVisuals description]
   * @param  page          [description]
   * @param  count         [description]
   * @param  sortField     [description]
   * @param  config
   * @return               Observable
   */
  getPublicVisuals(page: number = 0, count: number = 20, config): Observable<Content[]> {
    return this.http.post<Content[]>(`${this.envService.CONTENT_API}/searchpublic/${page}/${count}`, config);
  }

  /**
   * [getPublicVisuals description]
   * @param  page          [description]
   * @param  count         [description]
   * @param  config
   * @return               Promise
   */
  getPublicVisualsPromise(page: number = 0, count: number = 20, config): Promise<Content[]> {
    // const key = makeStateKey<string>(JSON.stringify({name: 'getPublicVisualsPromise', page, count, config }));
    // if(this.transferState.hasKey(key)){
    //   const data = this.transferState.get(key, "");
    //   return (data as any)
    // }
    return this.http
      .post<Content[]>(`${this.envService.CONTENT_API}/searchpublic/${page}/${count}`, config)
      .pipe(
        tap((data) => {
          // this.transferState.set(key, data as any);
        }),
      )
      .toPromise();
  }

  getFeaturedVisuals(page: number = 0, count: number = 20, config): Observable<Content[]> {
    return this.http.post<Content[]>(`${this.envService.CONTENT_API}/searchfeatured/${page}/${count}`, config);
  }

  getFeaturedVisualsPromise(page: number = 0, count: number = 20, config): Promise<Content[]> {
    // const key = makeStateKey<string>(JSON.stringify({name: 'getFeaturedVisualsPromise',page, count, config }));
    // if(this.transferState.hasKey(key)){
    //   const data = this.transferState.get(key, "");
    //   return (data as any)
    // }
    return this.http
      .post<Content[]>(`${this.envService.CONTENT_API}/searchfeatured/${page}/${count}`, config)
      .pipe(
        tap((data) => {
          //this.transferState.set(key, data);
        }),
      )
      .toPromise();
  }

  getCovidVsualsPromise(page: number = 0, count: number = 20, config): Promise<Content[]> {
    return this.http.post<Content[]>(`${this.envService.CONTENT_API}/searchcovid/${page}/${count}`, config).toPromise();
  }

  getMyVisuals(page: number = 0, count: number = 20, config): Observable<Content[]> {
    return this.http.post<Content[]>(`${this.API_PATH}/searchmy/${page}/${count}`, config);
  }

  getMyVisualsPromise(page: number = 0, count: number = 20, config): Promise<Content[]> {
    return this.http.post<Content[]>(`${this.API_PATH}/searchmy/${page}/${count}`, config).toPromise();
  }

  getPublicFromUserData(page: number = 0, count: number = 20, userId: string, config: any): Promise<Content[]> {
    return this.http.post<Content[]>(`${this.envService.CONTENT_API}/searchfromuserdata/${userId}/${page}/${count}`, config).toPromise();
  }

  /**
   * CRUD on Content Item
   */

  getStubByTypePromise(type: string, payload: any): any {
    return this.http.post<any>(this.CONTENT_STUB_PATH + '/' + type, payload).toPromise();
  }

  getContentBySlug(contentSlug: string): Observable<Content> {
    // console.log('[SERVERREQUEST] - getContentBySlugPromise ', contentSlug, `${this.API_PATH}/content/byslug/${cleanSlug(contentSlug)}`);

    return this.http.get<Content>(`${this.API_PATH}/content/byslug/${cleanSlug(contentSlug)}`);
  }

  getContentBySlugPromise(contentSlug: string): Promise<Content> {
    return this.http.get<Content>(`${this.API_PATH}/content/byslug/${cleanSlug(contentSlug)}`).toPromise();
  }

  getPublicContentBySlug(contentSlug: string): Observable<Content> {
    // const timeCache = generateTimeCache(15);
    return this.http.get<Content>(`${this.envService.ContentPublic}/byslug/${contentSlug}`, { headers: { NoAuth: 'true' } });
  }

  getPublicContentBySlugPromise(contentSlug: string): Promise<Content> {
    //console.log('[SERVERREQUEST] - getPublicContentBySlugPromise ', contentSlug);
    const timeCache = generateTimeCache(15);
    return this.http.get<Content>(`${this.envService.ContentPublic}/byslug/${contentSlug}`, { headers: { NoAuth: 'true' } }).toPromise();
  }

  async getPublicContentBySlugEmbed(contentSlug: string): Promise<Content> {
    // const timeCache = generateTimeCache(60);
    // const timeCacheBucket = generateTimeCache(60 * 5);

    const cdnLink = this.globalService.getCDNLink();
    const contentLink = `${cdnLink}/${contentSlug}/content.json`;

    const content = await firstValueFrom(
      this.http
        .get<Content>(contentLink, { headers: { 'Cache-Control': 'no-cache', NoAuth: 'true' } })
        .pipe(catchError((error) => this.http.get<Content>(`${this.envService.ContentPublic}/byslug/${contentSlug}`, { headers: { NoAuth: 'true' } }))),
    );

    validateBucketContent(content);

    return content;

    // const item = await this.http.get<Content>(`${ContentPublic}/byslug/${contentSlug}`, { headers: { NoAuth: 'true' } }).toPromise();
    // return item;
  }

  getContentFromBucket(slug): Promise<Content> {
    return this.http.get<Content>(`${this.API_PATH}/content/bucket/${slug}`).toPromise();
  }

  createContent(content: Content): Observable<ContentResponse> {
    return this.http.post<ContentResponse>(`${this.API_PATH}/content`, content);
  }

  createContentPromise(content: Content): any {
    return this.http.post<ContentResponse>(`${this.API_PATH}/content`, content).toPromise();
  }

  createFromExistingChildless(content: Content): Observable<ContentResponse> {
    return this.http.post<ContentResponse>(`${this.API_PATH}/content/createfromexistingchildless`, content);
  }

  createFromExistingChildlessPromise(content: Content): Promise<ContentResponse> {
    return this.http.post<ContentResponse>(`${this.API_PATH}/content/createfromexistingchildless`, content).toPromise();
  }

  updateContent(content: Content): Observable<ContentResponse> {
    return this.http.put<ContentResponse>(`${this.API_PATH}/content`, content);
  }

  updateContentPromise(content: Content): Promise<ContentResponse> {
    return this.http.put<ContentResponse>(`${this.API_PATH}/content`, content).toPromise();
  }

  updateContentWithChilren(updateObject: { content: Content; children: [any] }): Observable<ContentResponse> {
    return this.http.put<ContentResponse>(`${this.API_PATH}/content/updatewithchildren`, updateObject);
  }

  updateContentWithChilrenPromise(updateObject: { content: Content; children: [any] }): Promise<any> {
    return this.http.put<any>(`${this.API_PATH}/content/updatewithchildren`, updateObject).toPromise();
  }

  restoreContent(id: string, deleted_at: any): Observable<ContentResponse> {
    return this.http.post<ContentResponse>(`${this.API_PATH}/content/restore`, {
      _id: id,
      deleted_at: deleted_at,
    });
  }

  restoreContentPromise(id: string, deleted_at: any): Promise<ContentResponse> {
    return this.http
      .post<ContentResponse>(`${this.API_PATH}/content/restore`, {
        _id: id,
        deleted_at: deleted_at,
      })
      .toPromise();
  }

  deleteContent(id: string): Observable<ContentResponse> {
    return this.http.delete<ContentResponse>(`${this.API_PATH}/content/${id}`);
  }
  deleteContentPromise(id: string): Promise<ContentResponse> {
    return this.http.delete<ContentResponse>(`${this.API_PATH}/content/${id}`).toPromise();
  }

  // TODO replace mdc route with new routes that uses updateDataSet or createDataSet
  _createDataSetVersionedPromise(obj: any): Promise<any> {
    return this.http.post<any>(`${this.MDC_PATH}/versioned/datasetwithdata/create`, obj).toPromise();
  }

  _cloneDataSetVersionedPromise(obj: any): Promise<any> {
    return this.http.post<any>(`${this.MDC_PATH}/versioned/datasetwithdata/clone`, obj).toPromise();
  }

  /**
   * TODO replace with v2 create report and atlas asap
   * @param payload
   */
  createChildrenTypePromise(payload): any {
    return this.http.post<ContentResponse>(`${this.API_PATH}/createchildrentype`, payload).toPromise();
  }
  /**
   * TODO replace with v2 used by garbage
   * @param page
   * @param count
   */
  getGarbage(page: number = 0, count: number = 12): Observable<Content[]> {
    return this.http.get<Content[]>(`${this.GARBAGE_PATH}/get/${page}/${count}`).pipe(tap((data) => console.log('Garbage:', data)));
  }

  // silent admin boost route.. pshhh nobody has to know
  boostContent(slug: string): Promise<any> {
    return this.http.patch(`${this.API_V2_PATH}/${slug}/metrics/boost`, null).toPromise();
  }

  public getContentParent = async (slug: string, parentType = 'other', values = 'slug'): Promise<any> => {
    return this.http.get(`${this.API_V2_PATH}/${slug}/parent?parenttype=${parentType}&values=${values}`).toPromise();
  };
  /**
   * gets Content List
   *
   * @param page
   * @param count
   * @param order_by
   * @param space
   * @param type type string
   * @param parent contentslug
   * @param owner userslug
   * @param tags array of tag slugs
   * @param query search term (urlEncoded)
   * @param projection what will be returned | card | full | slug
   * @param includesubfolders optional - yes or no
   * @param rootshares optional - only has affect in space, all, myandshared, and sharedwithme returns only direct shares
   */
  getContentList = (
    page: number = 0,
    page_size: number = 12,
    order_by: string = 'updated_at desc',
    space: string = 'my',
    type = undefined,
    parent = undefined,
    owner = undefined,
    tags = [],
    query = undefined,
    projection = undefined,
    includesubfolders = undefined,
    rootshares = undefined,
    dataset = undefined,
  ): Observable<{ payload: Content[]; action: string; status: string }> => {
    let url = `${this.API_V2_PATH}?space=${space}&order_by=${order_by}&page=${page}&page_size=${page_size}`;
    if (type) {
      url = `${url}&type=${type}`;
    }
    if (parent) {
      url = `${url}&parent=${parent}`;
    }
    if (owner) {
      url = `${url}&parent=${owner}`;
    }
    if (tags && tags.length > 0) {
      url = `${url}&tags=${tags.join('|')}`;
    }
    if (query?.length) {
      query = query.replace(/[&\/\\#,+()$~%.'":*?!<>{}]/g, '');
      url = `${url}&query=${encodeURIComponent(query)}`;
    }
    if (projection) {
      url = `${url}&projection=${projection}`;
    }
    if (includesubfolders) {
      url = `${url}&includesubfolders=${includesubfolders}`;
    }
    if (rootshares) {
      url = `${url}&rootshares=${rootshares}`;
    }
    if (dataset) {
      url = `${url}&dataset=${dataset}`;
    }
    return this.http.get<{ payload: Content[]; action: string; status: string }>(url);
  };

  createShortcut(content: Content, folder: Content, fileName: string) {
    return this.http.post<any>(`${this.API_V2_PATH}/shortcut`, {
      payload: { folderSlug: folder ? folder.slug : 'root', contentSlug: content.slug, name: fileName || content.fileName || content.name },
    });
  }

  moveContent(contentSlug: string, folderSlug: string) {
    return this.http.patch<any>(`${this.API_V2_PATH}/${contentSlug}/move`, { payload: { folderSlug } });
  }

  updateAccess(content: Content, accessLevel: string) {
    return this.http.patch<any>(`${this.API_V2_PATH}/${content.slug}/access`, { payload: accessLevel });
  }

  cloneItem(slug: string, folderSlug?: string): Observable<{ payload: { slug: string; folderSlug: string } }> {
    return this.http.post<{ payload: { slug: string; folderSlug: string } }>(`${this.API_V2_PATH}/${cleanSlug(slug)}/clone`, {
      options: {
        folderSlug,
      },
    });
  }

  public getPublicContentList = (
    page: number = 0,
    page_size: number = 12,
    order_by: string = 'updated_at desc',
    dummySpace: any,
    type = undefined,
    parent = undefined,
    owner = undefined,
    tags = [],
    query = undefined,
    projection = undefined,
    includesubfolders = undefined,
    rootshares = undefined,
    dataset = undefined,
  ): Observable<{ payload: Content[]; action: string; status: string }> => {
    interface StringMap {
      [key: string]: string;
    }

    const parameters: StringMap = {
      space: 'all',
      order_by: order_by,
      page: String(page),
      page_size: String(page_size),
    };

    if (type) parameters.type = type;
    if (owner) parameters.owner = owner;
    if (parent) parameters.parent = parent;
    if (dataset) parameters.dataset = dataset;
    if (projection) parameters.projection = projection;
    if (rootshares) parameters.rootshares = rootshares;
    if (includesubfolders) parameters.includesubfolders = includesubfolders;

    if (tags && tags.length > 0) parameters.tags = tags.join('|');
    if (query && query.length > 0) parameters.query = encodeURIComponent(query.replace(/[&\/\\#,+()$~%.'":*?!<>{}]/g, ''));

    // const timeCache = generateTimeCache(60 * 5); // 5min
    // parameters.cache = String(timeCache);

    const queryString = new URLSearchParams(parameters).toString();
    return this.http.get<{ payload: Content[]; action: string; status: string }>(`${this.envService.ContentPublic}/search?${queryString}`, {
      headers: { NoAuth: 'true' },
    });
  };

  updateContentFileName(slug, fileName): Observable<ContentResponse> {
    return this.getContentBySlug(slug).pipe(
      map((content) => {
        content.fileName = fileName;
        return content;
      }),
      switchMap((content) => {
        // if (ParentTypes.includes(content.type)) {
        //   return this.updateContentWithChilren({ content, children: content.typeSpecific[content.type].children });
        // }

        return this.updateContent(content);
      }),
      take(1),
    );
  }
}
